import { ReactNode, Suspense, useEffect, useMemo, useRef } from 'react';
import {
  BrowserRouter,
  Location,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { RouteChangeListener } from '../../contexts/ThemeContext';
import { useAuth } from '../../hooks/useAuth';
import { useMatchedRoute } from '../../hooks/useMatchedRoute';
import { prefetchAppData } from '../../utils/dataPrefetch';
import { routeConfig, RouteConfig } from '../../utils/routeConfig';
import { OnboardingOverlay, OnboardingTrigger } from '../features/Onboarding';
import { PageTransitionStage, resolveTransition } from './PageTransition';
import { AuthMiddleware } from './AuthMiddleware';
import LoadingSpinner from './LoadingSpinner';

/**
 * Routing architecture:
 *
 *   BrowserRouter
 *   └─ AppShell                         ← resolves Layout + transition per match
 *      └─ <Layout>                      ← stays mounted across same-layout navs
 *         └─ <PageTransitionStage>      ← single-slot, path-keyed enter animation
 *            └─ <Suspense fallback=null>
 *               └─ <Routes location>    ← no per-route layout wrapper here
 *
 * Why this shape:
 * - Previous version wrapped each <Route>'s element in its layout AND used
 *   `key={location.pathname}` on <Routes>, which remounted the whole tree
 *   (including BottomNavigator) on every navigation. By hoisting layout
 *   resolution up, same-layout navigations (tab ↔ tab ↔ detail page) keep
 *   the chrome mounted and just swap the inner page content.
 * - The transition stage sits inside the layout so animations only affect
 *   the content area — the bottom nav, app bar, etc. don't move during
 *   navigation.
 */

const DefaultLayout: React.ComponentType<{ children: ReactNode; hideAppbar?: boolean }> = ({
  children,
}) => <>{children}</>;

/**
 * Render the routes tree WITHOUT the per-route layout wrapper. Layout
 * resolution happens in AppShell so we can keep a single layout instance
 * mounted across same-layout navigations.
 */
const renderInnerRoutes = (routes: RouteConfig[]): ReactNode =>
  routes.map(route => {
    const RouteComponent = route.component;
    return (
      <Route
        key={route.path}
        path={route.path}
        element={
          <AuthMiddleware requireAuth={route.protected}>
            <RouteComponent />
          </AuthMiddleware>
        }
      >
        {route.children?.length ? renderInnerRoutes(route.children) : null}
      </Route>
    );
  });

interface AppShellProps {
  isAuthenticated: boolean;
}

const AppShell = ({ isAuthenticated }: AppShellProps) => {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Previous location ref — used to compute push/pop direction. On the
  // very first render it equals the current location, so resolveTransition
  // just returns a noop-ish result that the stage ignores anyway.
  const prevLocationRef = useRef<Location>(location);
  const prevPathname = prevLocationRef.current.pathname;

  const currMatch = useMatchedRoute(location.pathname);
  const prevMatch = useMatchedRoute(prevPathname);

  // Resolve transition only when the pathname actually changes. Query-param
  // writes from AssetsContext/OverviewContext reuse the same pathname but
  // mint a new location.key; we deliberately don't re-resolve for those.
  const resolved = useMemo(
    () =>
      resolveTransition(prevPathname, location.pathname, prevMatch, currMatch, navigationType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname],
  );

  useEffect(() => {
    prevLocationRef.current = location;
  }, [location.pathname, location]);

  const Layout = currMatch?.layout ?? DefaultLayout;
  const hideAppbar = currMatch?.hideAppbar ?? false;

  const innerRoutes = useMemo(() => renderInnerRoutes(routeConfig), []);

  return (
    <Layout hideAppbar={hideAppbar}>
      <PageTransitionStage
        location={location}
        transition={resolved.type}
        direction={resolved.direction}
      >
        {slotLocation => (
          <Suspense fallback={null}>
            <Routes location={slotLocation}>
              {innerRoutes}
              <Route
                path="*"
                element={
                  isAuthenticated ? (
                    <Navigate to={ROUTES.ASSETS} replace />
                  ) : (
                    <Navigate to={ROUTES.HOME} replace />
                  )
                }
              />
            </Routes>
          </Suspense>
        )}
      </PageTransitionStage>
    </Layout>
  );
};

const AppRoutes = () => {
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      prefetchAppData();
    }
  }, [isAuthenticated, isLoading]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <BrowserRouter>
      <RouteChangeListener />
      <AppShell isAuthenticated={isAuthenticated} />
      <OnboardingOverlay />
      <OnboardingTrigger />
    </BrowserRouter>
  );
};

export default AppRoutes;
